import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { forwardRef, useEffect } from 'react'

import { AppAvatar } from '@src/app/components'
import { useAppStore } from '@src/app/context'
import { ErrorBoundary } from '@src/app/error'
import { MessageBubble } from '@src/app/voice/incoming-call/message-bubble'
import useCallError from '@src/app/voice/useCallError'
import Button from '@src/component/ButtonV2'
import type { ButtonProps } from '@src/component/button'
import useKeyboardShortcuts from '@src/lib/hooks/useKeyboardShortcuts'
import type { RoomInvitationType } from '@src/service/transport/voice'
import type { ActiveCall } from '@src/service/voice'
import Tooltip from '@ui/Tooltip'
import Typography from '@ui/Typography'
import { AiIcon } from '@ui/icons/custom'
import { CallEndIcon, CallAcceptIcon, ListenerIcon } from '@ui/icons/tint/20/general'

import CallHeader from './CallHeader'
import * as styles from './IncomingCall.css'
import IncomingCallDescription from './IncomingCallDescription'

interface IncomingCallProps {
  call: ActiveCall
  blurred: boolean
}

const getSharedInboxInfo = (type: RoomInvitationType) => {
  if (type === 'transferred') {
    return 'Declining a transfer request still allows others in the shared inbox to accept'
  }

  return 'Declining a call still allows others in the shared inbox to pick up'
}

const getAcceptCallActionText = (call: ActiveCall, type: RoomInvitationType) => {
  if (type === 'added') {
    if (call.isListener) {
      return 'Listen in'
    } else {
      return 'Join Call'
    }
  }

  return 'Accept'
}

const RejectButton = forwardRef<
  HTMLButtonElement,
  {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
    blurred: boolean
  } & Omit<ButtonProps, 'color' | 'className' | 'ref'>
>(({ onClick, blurred, children, ...props }, ref) => {
  return (
    // eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button
    <Button
      color="danger"
      onClick={onClick}
      ref={ref}
      {...props}
      className={blurred ? styles.actionButtonRejectBlurred : undefined}
    >
      <CallEndIcon className={styles.actionIcon} />
      {children}
    </Button>
  )
})

const IncomingCall = function ({ call, blurred }: IncomingCallProps) {
  const app = useAppStore()
  const { notification, command, service } = app
  const ongoingCalls = service.voice.ongoingCalls
  const phoneNumber = call.participants.toPhoneNumber

  useCallError(call)

  const footerText =
    !call.participants.toMember && phoneNumber?.isShared && call.invitation.type
      ? getSharedInboxInfo(call.invitation.type ?? 'called')
      : ongoingCalls.length > 0
      ? 'Accepting this call will end the current call you are on'
      : null

  const caller =
    call.invitation.type === 'transferred'
      ? call.participants.transferrer
      : call.participants.from

  useEffect(() => {
    const close = notification.show({
      title:
        (caller?.isSuggestedContact ? `Maybe: ${caller?.name}` : caller?.name) ??
        'Incoming call',
      id: call.id,
      body: call.phoneNumber
        ? `📞 Incoming call to ${call.phoneNumber?.formattedName}...`
        : `📞 Incoming team call...`,
      onClick: () => app.focus(),
      onAction: (index: number) => {
        if (index === 0) {
          handleAccept()
        } else if (index === 1) {
          call.reject()
        }
      },
    })
    return close
    // We want this to change only when `open` changes to avoid multiple notifications (since we can't update a notification)
    // eslint-disable-next-line react-compiler/react-compiler -- UXP-3732 - Fix React Compiler errors
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useKeyboardShortcuts({
    name: 'dialer/incoming-call',
    node: document,
    handler: (shortcut, event) => {
      if (!open) {
        return
      }
      if (shortcut === 'Escape') {
        event.preventDefault()
        call.reject()
      }
    },
    dep: [open, call],
  })

  const handleAccept = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    call.accept()
  }

  const handleReject =
    (withKey: boolean) => (event: React.MouseEvent<HTMLButtonElement>) => {
      call.reject()

      if (withKey && (event.ctrlKey || event.metaKey)) {
        command.present({
          name: 'reject call with message',
          onSelect: (body) => {
            command.hide()
            const to = caller?.identifier
            if (!to) {
              return
            }
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
            service.activity.sendRaw({
              ...(call.isDirectCall
                ? {
                    directNumberId: service.user.directNumber?.id,
                  }
                : {
                    phoneNumberId: call.phoneNumber?.id,
                  }),
              to,
              body,
            })
          },
        })
      }
    }

  return (
    <div className={styles.root({ blurred })}>
      <CallHeader call={call} blurred={blurred} />
      <div className={styles.content}>
        <AppAvatar
          identity={caller?.isSuggestedContact ? undefined : caller?.identity}
          size={80}
        />
        <Typography
          variant="body"
          color="textPrimary"
          fontWeight="medium"
          className={styles.name}
        >
          {caller?.isSuggestedContact ? (
            <>
              <Typography component="span" color="textTertiary">
                <AiIcon className={styles.aiIcon} />
                Maybe:
              </Typography>{' '}
            </>
          ) : null}
          {caller?.name} {call.isVerified && <span style={{ marginLeft: 5 }}>✅</span>}
        </Typography>

        <ErrorBoundary fallback={<></>}>
          <div
            className={styles.callDescription({
              type:
                typeof call.phoneMenuSelection === 'string' ? 'phone-menu' : 'invitation',
            })}
          >
            <IncomingCallDescription
              uiStore={call.ui.incomingCall}
              phoneMenuSelection={call.phoneMenuSelection}
            />
          </div>
        </ErrorBoundary>
        {call.invitation.message && (
          <MessageBubble
            classes={{ root: styles.messageBubble }}
            message={call.invitation.message}
          />
        )}
      </div>
      {call.hasDeniedMicrophonePermissions ? (
        <div className={styles.microphonePermissions}>
          <Typography variant="callout" color="error" className={styles.title}>
            You must enable microphone permissions in your browser to accept incoming
            calls.
          </Typography>
        </div>
      ) : null}
      <div className={cx(styles.actions, { [styles.actionsPadding]: !footerText })}>
        <Tooltip
          title={'Hold ⌘ to reject with message'}
          disabled={!call.participants.toMember && phoneNumber?.isShared}
          placement="top"
          themeKey="dark"
          colorScheme="call"
        >
          {!call.participants.toMember && phoneNumber?.isShared ? (
            <RejectButton onClick={handleReject(false)} blurred={blurred}>
              Decline
            </RejectButton>
          ) : (
            <RejectButton onClick={handleReject(true)} blurred={blurred}>
              Reject
            </RejectButton>
          )}
        </Tooltip>
        <Tooltip
          title="You cannot accept calls without enabling microphone permissions."
          disabled={!call.hasDeniedMicrophonePermissions}
          placement="top"
          themeKey="dark"
          colorScheme="call"
        >
          {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
          <Button
            disabled={call.hasDeniedMicrophonePermissions}
            className={blurred ? styles.actionButtonConfirmBlurred : undefined}
            color={call.isListener ? 'primary' : 'secondaryGreen'}
            onClick={handleAccept}
          >
            {call.isListener ? (
              <ListenerIcon className={styles.actionIcon} />
            ) : (
              <CallAcceptIcon className={styles.actionIcon} />
            )}
            {call.invitation.type
              ? getAcceptCallActionText(call, call.invitation.type)
              : null}
          </Button>
        </Tooltip>
      </div>
      {footerText && (
        <div className={styles.footerContainer}>
          <Typography variant="caption1" className={styles.footerText}>
            {footerText}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default observer(IncomingCall)
